import { vibes, subjectGroups, artStyles, productTypes } from './ts-options.js';
import { initialState, productIssue, resolveVariant, valuesFor, choiceAvailable, toggleSubject, validation, cartPayload } from './ts-model.js';

const swatches = { white: '#fff', black: '#161616', red: '#d93932', blue: '#2862bd', pink: '#ed9ab7', 'heather grey': '#a0a0a0' };

class SlopBuilder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.state = initialState();
    this.products = JSON.parse(this.querySelector('[data-products]').textContent);
    this.busy = false;
    this.form = this.querySelector('form');
    this.renderChoices('productType', Object.entries(productTypes));
    this.renderChoices('vibe', vibes.map(value => [value, value]));
    this.renderChoices('artStyle', artStyles.map(value => [value, value]));
    const container = this.querySelector('[data-choices="subjects"]');
    for (const [category, values] of Object.entries(subjectGroups)) {
      const group = document.createElement('div');
      group.className = 'subject-group';
      const title = document.createElement('h4');
      title.textContent = category;
      group.append(title);
      const chips = document.createElement('div');
      chips.className = 'choices';
      values.forEach(value => chips.append(this.choice('subjects', value, value)));
      group.append(chips);
      container.append(group);
    }
    this.addEventListener('click', event => {
      const button = event.target.closest('button[data-key]');
      if (!button || this.busy || button.disabled) return;
      const { key, value } = button.dataset;
      if (key === 'subjects') this.state.subjects = toggleSubject(this.state.subjects, value);
      else this.state[key] = value;
      if (key === 'productType') {
        this.state.size = null;
        this.state.color = null;
        this.renderPhysical();
      }
      if (key === 'color' && this.state.size && !choiceAvailable(this.state, this.products, 'size', this.state.size)) this.state.size = null;
      this.querySelector('[data-error]').textContent = '';
      this.update();
      if (button.dataset.remove !== undefined) this.querySelector(`button[data-key="subjects"][data-value="${CSS.escape(value)}"]`)?.focus();
    });
    this.querySelector('[name="acknowledged"]').addEventListener('change', event => {
      this.state.acknowledged = event.target.checked;
      this.update();
    });
    this.form.addEventListener('submit', event => { event.preventDefault(); this.submit(); });
    this.querySelector('[name="extraSpice"]').addEventListener('input', event => {
      this.state.extraSpice = event.target.value;
      this.update();
    });
    this.querySelector('[data-loading]').hidden = true;
    this.querySelector('[data-interface]').hidden = false;
    this.renderPhysical();
    this.update();
  }

  choice(key, value, label) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'choice';
    button.dataset.key = key;
    button.dataset.value = value;
    button.setAttribute('aria-pressed', 'false');
    if (key === 'color') {
      const swatch = document.createElement('span');
      swatch.className = 'swatch';
      swatch.setAttribute('aria-hidden', 'true');
      swatch.style.background = swatches[value.toLowerCase()] || 'repeating-linear-gradient(45deg,#fff 0 4px,#aaa 4px 8px)';
      button.append(swatch);
    }
    const text = document.createElement('span');
    text.textContent = label;
    button.append(text);
    return button;
  }

  renderChoices(key, values) {
    this.querySelector(`[data-choices="${key}"]`).replaceChildren(...values.map(([value, label]) => this.choice(key, value, label)));
  }

  renderPhysical() {
    const product = this.products[this.state.productType];
    for (const key of ['color', 'size']) this.renderChoices(key, valuesFor(product, key).map(value => [value, value]));
    this.querySelector('[data-product-note]').textContent = this.state.productType ? productIssue(product) || 'Choose your color, then your size. Unavailable choices are disabled.' : 'Choose a shirt type to load its available colors and sizes.';
  }

  update() {
    const errors = validation(this.state, this.products);
    this.querySelectorAll('button[data-key]').forEach(button => {
      const { key, value } = button.dataset;
      const selected = key === 'subjects' ? this.state.subjects.includes(value) : this.state[key] === value;
      button.setAttribute('aria-pressed', String(selected));
      button.disabled = this.busy || (key === 'subjects' && !selected && this.state.subjects.length >= 3) || (['size', 'color'].includes(key) && !choiceAvailable(this.state, this.products, key, value)) || (key === 'productType' && Boolean(productIssue(this.products[value])));
    });
    this.querySelector('[data-count]').textContent = `${this.state.subjects.length} / 3 selected${this.state.subjects.length === 3 ? ' — remove one to choose another.' : ''}`;
    const selected = this.state.subjects.map(value => {
      const button = this.choice('subjects', value, `${value} ×`);
      button.dataset.remove = '';
      button.setAttribute('aria-label', `Remove ${value}`);
      button.setAttribute('aria-pressed', 'true');
      button.disabled = this.busy;
      return button;
    });
    this.querySelector('[data-selected-subjects]').replaceChildren(...selected);
    const variant = resolveVariant(this.state, this.products);
    const extraSpice = this.state.extraSpice.trim();
    this.querySelector('[data-extra-spice-review]').hidden = !extraSpice;
    this.querySelector('[data-summary="extraSpice"]').textContent = extraSpice;
    this.querySelector('[data-spice-count]').textContent = `${this.state.extraSpice.length} / 25`;
    this.querySelector('[name="extraSpice"]').disabled = this.busy;
    const summary = { productType: productTypes[this.state.productType], vibe: this.state.vibe, subjects: this.state.subjects.join(' + '), artStyle: this.state.artStyle, color: this.state.color, size: this.state.size, price: variant?.priceLabel };
    for (const [key, value] of Object.entries(summary)) this.querySelector(`[data-summary="${key}"]`).textContent = value || 'Not selected';
    this.querySelector('[data-status]').textContent = errors.length ? 'STATUS: AWAITING INGREDIENTS' : 'STATUS: READY TO COOK';
    this.querySelector('[data-validation]').textContent = errors.length ? errors.join(' ') : 'All ingredients loaded. Your mystery shirt is ready for the cart.';
    const submit = this.querySelector('[data-submit]');
    submit.disabled = this.busy || errors.length > 0;
    submit.textContent = this.busy ? 'ADDING YOUR SLOP…' : this.dataset.cta;
    this.querySelector('[name="acknowledged"]').disabled = this.busy;
    this.form.setAttribute('aria-busy', String(this.busy));
  }

  async submit() {
    if (this.busy) return;
    const errorBox = this.querySelector('[data-error]');
    errorBox.textContent = '';
    let payload;
    try { payload = cartPayload(this.state, this.products); }
    catch (error) { errorBox.textContent = error.message; errorBox.focus(); return; }
    this.busy = true;
    this.update();
    try {
      const root = window.Shopify?.routes?.root || this.dataset.root;
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : 'Shopify could not add this shirt. Please review your selections and try again.');
      if (!Array.isArray(result.items) || !result.items.some(item => String(item.variant_id ?? item.id) === String(payload.items[0].id))) throw new Error('The cart response was unexpected. Check your cart before trying again.');
      window.location.assign(this.dataset.cart);
    } catch (error) {
      errorBox.textContent = `${error.message || 'Connection interrupted.'} Your selections are saved here. If the connection dropped, check your cart before retrying to avoid adding twice.`;
      this.busy = false;
      this.update();
      errorBox.focus();
    }
  }
}
if (!customElements.get('slop-builder')) customElements.define('slop-builder', SlopBuilder);
